export interface SupabaseErrorLike {
  code?: string | null;
  status?: number | string | null;
  message?: string | null;
  details?: string | null;
  hint?: string | null;
}

const getMessageText = (value: unknown) => {
  if (!value) return "";
  return String(value).toLowerCase();
};

const getErrorCode = (error: SupabaseErrorLike) => {
  return String(error?.code ?? "").toUpperCase();
};

const getStatus = (error: SupabaseErrorLike) => {
  if (typeof error.status === "string") {
    const parsed = Number.parseInt(error.status, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return error.status ?? null;
};

const extractStatusFromText = (value: unknown, message: string, details: string, hint: string) => {
  const text = getMessageText(value).toLowerCase();
  const combined = `${text} ${message} ${details} ${hint}`;
  const match = combined.match(/status(?:\s*code)?\s*[:=]\s*(\d{3})/i);
  if (match) {
    const parsed = Number.parseInt(match[1], 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const isServerError = (error: unknown) => {
  if (!error || typeof error !== "object") return false;
  const supabaseError = error as SupabaseErrorLike;
  const status = getStatus(supabaseError);
  if (typeof status === "number" && status >= 500 && status <= 599) return true;
  if (typeof status === "string") return false;

  const message = getMessageText(supabaseError.message);
  const details = getMessageText(supabaseError.details);
  const hint = getMessageText(supabaseError.hint);
  const textStatus = extractStatusFromText(supabaseError.code, message, details, hint);
  return typeof textStatus === "number" && textStatus >= 500 && textStatus <= 599;
};

const hasSchemaCacheSignal = (message: string, details: string, hint: string) => {
  return (
    message.includes("schema cache") ||
    message.includes("could not find the table") ||
    message.includes("could not find the function") ||
    message.includes("could not find the relation") ||
    message.includes("relation") && message.includes("does not exist") ||
    message.includes("column") && message.includes("does not exist") ||
    message.includes("does not exist") ||
    details.includes("could not find") ||
    hint.includes("perhaps you meant")
  );
};

export const isSupabaseSchemaError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const supabaseError = error as SupabaseErrorLike;

  const code = getErrorCode(supabaseError);
  const status = getStatus(supabaseError);
  const message = getMessageText(supabaseError.message);
  const details = getMessageText(supabaseError.details);
  const hint = getMessageText(supabaseError.hint);
  const combined = `${message} ${details} ${hint}`.toLowerCase();

  const schemaErrorCodes = new Set([
    "PGRST202", // RPC/function missing
    "PGRST205", // Table/rpc missing in schema cache
    "PGRST301", // RLS not relevant, but keep explicit for safety
    "42P01", // undefined table
    "42703", // undefined column
    "42883", // undefined function
  ]);

  if (schemaErrorCodes.has(code)) return true;

  // Relation/column missing in schema cache often returns 404/400 in the REST API.
  if (status === 404 || status === 400) {
    if (combined.includes("public.") || hasSchemaCacheSignal(message, details, hint)) return true;
  }

  return (
    hasSchemaCacheSignal(message, details, hint) &&
    (message.includes("could not find") || message.includes("does not exist") || message.includes("schema"))
  );
};

export const isSupabasePermissionError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const supabaseError = error as SupabaseErrorLike;

  const status = getStatus(supabaseError);
  const code = getErrorCode(supabaseError);
  const message = getMessageText(supabaseError.message);

  const isExplicitCode =
    code === "42501" ||
    code === "PGRST104" ||
    code === "PGRST116" ||
    code === "PGRST301";

  const isExplicitStatus = status === 401 || status === 403 || status === 419;

  const isPermissionMessage = message.includes("permission denied") ||
    message.includes("insufficient privilege") ||
    message.includes("not authenticated") ||
    message.includes("not authorized") ||
    message.includes("forbidden");

  return isExplicitCode || isExplicitStatus || isPermissionMessage;
};

export const isSupabaseUnavailableError = (error: unknown): boolean =>
  isSupabaseSchemaError(error) || isSupabasePermissionError(error) || isServerError(error);
